import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import * as ls from 'local-storage';
import { AxiosResponse } from 'axios';
import { useMutation, useQueryClient, UseMutateFunction } from 'react-query';
import { useIntercom } from 'react-use-intercom';
import { useUnleashContext } from '@unleash/proxy-client-react';

import axiosClient from '@hooks/api/axiosClient';

import { User, LoginResponse } from '@appTypes/SecureUser';

import { localStorageKeys } from '@utils/constants';

export interface ImpersonationIris {
  ownerIri: string;
  organisationIri: string;
}

interface AuthContextType {
  user: User;
  isLoggingIn: boolean;
  isSuccessLogin: boolean;
  isSuccessLogout: boolean;
  isLoggingOut: boolean;
  login: UseMutateFunction<AxiosResponse<LoginResponse, any>, unknown, { email: string; password: string }, unknown>;
  hasUserId: () => string;
  setUser: (arg?: User) => void;
  logout: UseMutateFunction<AxiosResponse<User, any>, unknown, void, unknown>;
  isErrorLogin: boolean;
  errorLogin: unknown;
  microsoftLogin: UseMutateFunction<AxiosResponse<LoginResponse, any>, unknown, { accessToken: string }, unknown>;
  isBusinessUser: () => boolean;
  hasBusinessRole: (role: string | string[]) => boolean;
  isPawprintAdminUser: boolean;
  isImpersonating: boolean;
  getPawprintAdminUserIri: () => string;
  impersonationIris: ImpersonationIris;
  setImpersonationIris: (impersonationIris: ImpersonationIris) => void;
  isPawprintAdminImpersonating: (isImpersonating: boolean) => boolean;
  userOrganisationIri: string;
}

const AuthContext = createContext<AuthContextType>(null);

const loginRequest = async ({ email, password }: { email: string; password: string }) =>
  axiosClient.post<LoginResponse>('login', { email, password });

const logoutRequest = async () => axiosClient.post<User>('logout');

const microsoftLoginRequest = async (req: { accessToken: string }) =>
  axiosClient.post<LoginResponse>('microsoft_login', req);

export interface AuthProviderProps {
  children?: React.ReactNode;
  user?: User;
}

const AuthProvider = ({ children, user: providedUser = null }: AuthProviderProps) => {
  const initialImpersonatingIris = { ownerIri: '', organisationIri: '' };

  const [user, setUser] = useState<User>(providedUser);
  const [impersonationIris, setImpersonationIris] = useState(initialImpersonatingIris);
  const queryClient = useQueryClient();
  const unleashContext = useUnleashContext();
  const { shutdown, boot } = useIntercom();

  // helpers

  const hasUserId = () => ls.get<string>(localStorageKeys.userId);

  const getPawprintAdminUserIri = () => ls.get<string>(localStorageKeys.pawprintAdminUser);

  const isPawprintAdminUser = Boolean(getPawprintAdminUserIri());

  const isImpersonating = useMemo(() => impersonationIris?.ownerIri?.length > 0, [impersonationIris.ownerIri]);

  const isPawprintAdminImpersonating = (checkImpersonating: boolean) =>
    isPawprintAdminUser && isImpersonating === checkImpersonating;

  const isBusinessUser = () => {
    // overide if it is Pawprint Admin they are impersonating
    if (isPawprintAdminImpersonating(true)) {
      return true;
    }
    return user?.userProfile?.organisation?.isBusiness || false;
  };

  const hasBusinessRole = (role: string | string[]): boolean => {
    let roles = role;
    // overide if it is Pawprint Admin they are impersonating
    if (isPawprintAdminImpersonating(true)) {
      return true;
    }

    if (typeof role === 'string') {
      roles = [role];
    }

    return roles.includes(user?.userProfile?.businessMembership?.role.reference);
  };

  // update the localStorage with the impersonationIri that will be used in the axoisClient to intercept requests and update the SWITCH_USER_HEADER
  useEffect(() => {
    if (isImpersonating) {
      ls.set(localStorageKeys.impersonationIri, impersonationIris.ownerIri);
    } else {
      ls.remove(localStorageKeys.impersonationIri);
    }
  }, [isImpersonating, impersonationIris.ownerIri]);

  // Update unleash context with a userID
  const userId = user?.userId;
  useEffect(() => {
    if (userId) {
      unleashContext({ userId });
    }
  }, [userId, unleashContext]);

  const userOrganisationIri = isImpersonating
    ? impersonationIris.organisationIri
    : user?.userProfile?.organisation?.iri;

  const onLoginSuccess = ({ data }) => {
    if (data.userIri.includes('admin_user')) {
      ls.set(localStorageKeys.pawprintAdminUser, data.userIri);
    }
    // here we rely on the returned data contains the user.
    ls.set(localStorageKeys.userId, data.userId);
  };

  const {
    mutate: login,
    isSuccess: isSuccessLogin,
    isLoading: isLoggingIn,
    isError: isErrorLogin,
    error: errorLogin,
    reset: resetLogin,
  } = useMutation(loginRequest, {
    onSuccess: onLoginSuccess,
  });

  const { mutate: microsoftLogin } = useMutation(microsoftLoginRequest, {
    onSuccess: onLoginSuccess,
  });

  const {
    mutate: logout,
    isSuccess: isSuccessLogout,
    isLoading: isLoggingOut,
  } = useMutation(logoutRequest, {
    onSuccess: () => {
      // Clear local storage items
      ls.remove(localStorageKeys.userId);
      ls.remove(localStorageKeys.secureUser);
      ls.remove(localStorageKeys.pawprintAdminUser);
      // clear user in auth provider this will trigger a rerender of RequireAuth component which will then push the user to the login page
      setUser(null);
      setImpersonationIris(initialImpersonatingIris);
      resetLogin();
      // Clear Intercom
      shutdown();
      boot();
      // clear query cache
      queryClient.clear();
      // clear session storage
      sessionStorage.clear();
    },
  });

  const value = {
    user,
    login,
    isSuccessLogin,
    isLoggingOut,
    isSuccessLogout,
    isLoggingIn,
    hasUserId,
    setUser,
    logout,
    isErrorLogin,
    errorLogin,
    microsoftLogin,
    isBusinessUser,
    hasBusinessRole,
    isPawprintAdminUser,
    isImpersonating,
    getPawprintAdminUserIri,
    impersonationIris,
    setImpersonationIris,
    isPawprintAdminImpersonating,
    userOrganisationIri,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => useContext(AuthContext);
export { useAuth, AuthProvider };
